import { AppService } from '@services/app/app.service';
import { Injectable } from '@angular/core';
import {
  AnalyticsCtaClickEventModel,
  AnalyticsErrorEventInputModel,
  AnalyticsErrorEventModel,
  AnalyticsEventDataModel,
  AnalyticsEventModel,
  AnalyticsEventName,
  AnalyticsInputFocusEventModel,
  AnalyticsOutcomeEventModel,
  AnalyticsPageviewEventModel,
  AnalyticsPopupEventModel
} from '@core/models/analytics';
import { Observable, Subject } from 'rxjs';
import { PositionService } from '../position/position.service';
import { truncateText } from '@core/utils';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private history: AnalyticsEventDataModel[] = [];

  private eventData$ = new Subject<AnalyticsEventDataModel>();

  constructor(
    private appService: AppService,
    private positionService: PositionService
  ) {}

  getEventDataStream(): Observable<AnalyticsEventDataModel> {
    return this.eventData$.asObservable();
  }

  pushPageViewEvent(page: { path: string; title: string }): void {
    const prevEvent =
      this.findEventOnHistory<AnalyticsPageviewEventModel>('virtual_pageview');

    if (prevEvent && prevEvent.page.path === page.path) {
      return;
    }

    this.pushEvent(
      'virtual_pageview',
      baseEvent =>
        ({
          ...baseEvent,
          page: {
            path: page.path,
            title: page.title
          },
          http: {
            response: {
              code: '200'
            }
          }
        } as AnalyticsPageviewEventModel)
    );
  }

  pushPopupEvent(popup: {
    title: string;
    content: string;
    ctaTexts: string[];
  }): void {
    this.pushEvent(
      'popup',
      baseEvent =>
        ({
          ...baseEvent,
          type: '',
          popup: {
            action: 'impression',
            elements: {
              title: popup.title,
              content: popup.content,
              name: null,
              cta: {
                existing:
                  popup.ctaTexts.length > 0
                    ? popup.ctaTexts.map(x => x.trim())
                    : null,
                clicked: null
              }
            }
          }
        } as AnalyticsPopupEventModel)
    );
  }

  pushPopupCtaClickEvent(cta: { text: string }): void {
    const impressionEvent = this.findEventOnHistory<AnalyticsPopupEventModel>(
      'popup',
      item => item.popup.action === 'impression'
    );

    if (!impressionEvent) {
      return;
    }

    this.pushEvent(
      'popup',
      baseEvent =>
        ({
          ...baseEvent,
          type: '',
          popup: {
            action: 'click',
            elements: {
              title: impressionEvent.popup.elements.title,
              content: impressionEvent.popup.elements.content,
              name: null,
              cta: {
                existing: impressionEvent.popup.elements.cta.existing,
                clicked: cta.text && cta.text.trim()
              }
            }
          }
        } as AnalyticsPopupEventModel)
    );
  }

  pushCtaClickEvent(cta: { text: string; class: string }): void {
    this.pushEvent(
      'cta_click',
      baseEvent =>
        ({
          ...baseEvent,
          cta: {
            text: cta.text && cta.text.trim(),
            class: cta.class,
            ['target_url']: null
          }
        } as AnalyticsCtaClickEventModel)
    );
  }

  pushInputFocusEvent(
    input: {
      label: string;
      id: string;
      class: string;
    },
    action: 'focusin' | 'focusout'
  ): void {
    this.pushEvent(
      'input_field_focus',
      baseEvent =>
        ({
          ...baseEvent,
          ['input_field']: {
            label: input.label,
            id: input.id,
            class: input.class
          },
          action
        } as AnalyticsInputFocusEventModel)
    );
  }

  pushInputBlurEvent(input: {
    label: string;
    id: string;
    class: string;
  }): void {
    this.pushEvent(
      'input_field_focus',
      baseEvent =>
        ({
          ...baseEvent,
          ['input_field']: {
            label: input.label,
            id: input.id,
            class: input.class
          },
          action: 'focusout'
        } as AnalyticsInputFocusEventModel)
    );
  }

  pushErrorEvent(input: AnalyticsErrorEventInputModel): void {
    this.pushEvent(
      'custom_error',
      baseEvent =>
        ({
          ...baseEvent,
          error: {
            ['error_type']: input.errorType,
            ['error_code']: input.errorCode,
            ['error_message']: truncateText(input.errorMessage, {
              maxLength: 100
            }),
            ['error_field']: input.errorField,
            ['is_fatal']: input.isFatal
          }
        } as AnalyticsErrorEventModel)
    );
  }

  pushOutcomeEvent(type: 'OK' | 'KO', message?: string): void {
    this.pushEvent(
      'request_outcome',
      baseEvent =>
        ({
          ...baseEvent,
          request: {
            id: this.positionService.getPositionId(),
            description: ''
          },
          response: {
            code: type === 'OK' ? 1 : 0,
            status: type === 'OK' ? 200 : 500,
            message:
              type === 'KO' && typeof message === 'string'
                ? truncateText(message, { maxLength: 100 })
                : undefined
          }
        } as AnalyticsOutcomeEventModel)
    );
  }

  isAnalyticsEventEnabled(name: AnalyticsEventName): boolean {
    const offList = this.appService.getConfig().analytics.eventsOff || [];

    return this.isAnalyticsEnabled() && !offList.includes(name);
  }

  isAnalyticsEnabled(): boolean {
    return this.appService.getConfig().analytics.enabled === true;
  }

  private getBaseEvent(name: AnalyticsEventName): AnalyticsEventModel {
    return {
      language: this.appService.getLocale().substring(0, 2),
      ['procedure_id']: this.positionService.getPositionId(),
      ['campaign_id']: '',
      event: name
    };
  }

  private pushEvent(
    name: AnalyticsEventName,
    fn: (baseEvent: AnalyticsEventModel) => AnalyticsEventModel
  ): void {
    if (!this.isAnalyticsEventEnabled(name) || typeof fn !== 'function') {
      return;
    }

    const event = fn(this.getBaseEvent(name));

    if (event) {
      this.history.unshift({ name: event.event, event });
      this.eventData$.next({ name: event.event, event });
    }
  }

  private findEventOnHistory<T>(
    name: AnalyticsEventName,
    finder?: (ev: T) => boolean
  ): T {
    const item = this.history.find(
      x =>
        x.name === name &&
        (typeof finder !== 'function' || finder(x.event as any))
    );

    if (item) {
      return item.event as any;
    }
  }
}
